/**
 * Shared address formatting utilities.
 * Used across driver dashboard, job cards, SMS, and all address displays
 * to ensure a single source of truth.
 */

/**
 * Extracts the first line of a comma-separated address string.
 * e.g. "37 Sunderland Rd, Sunderland, SR6 7UW" → "37 Sunderland Rd"
 */
export function getAddressLine1(address?: string | null): string {
  if (!address) return "";
  const line = address.split(",")[0]?.trim() || "";
  return line;
}

/**
 * Formats an address for display on job cards and SMS messages.
 * Shows Address Line 1 + Postcode only — the clearest format for drivers.
 *
 * @param address  Full address string (or just line 1)
 * @param postcode Postcode (may already be embedded in address)
 * @returns  e.g. "37 Sunderland Rd, SR6 7UW"
 */
export function formatAddressShort(
  address?: string | null,
  postcode?: string | null
): string {
  const line1 = getAddressLine1(address);
  const pc = (postcode ?? "").trim();

  if (!line1 && !pc) return "—";
  if (!line1) return pc;
  if (!pc) return line1;

  // Avoid duplicating postcode if it already appears in line1
  const normalise = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (normalise(line1).includes(normalise(pc))) return line1;

  return `${line1}, ${pc}`;
}

/**
 * Legacy helper – joins full address + postcode without duplication.
 * Kept for backward-compatibility in places that still show full addresses.
 */
const normalizeAlphaNum = (input: string) =>
  input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const includesNormalized = (haystack: string, needle: string) => {
  const h = normalizeAlphaNum(haystack);
  const n = normalizeAlphaNum(needle);
  if (!h || !n) return false;
  return h.includes(n);
};

export function joinAddressAndPostcode(
  address?: string | null,
  postcode?: string | null
): string {
  const a = (address ?? "").trim();
  const p = (postcode ?? "").trim();

  if (!a && !p) return "";
  if (!a) return p;
  if (!p) return a;
  if (includesNormalized(a, p)) return a;
  return `${a}, ${p}`;
}

/**
 * Opens the device's native maps app for navigation to the given address.
 * Uses Apple Maps on iOS and Google Maps on Android/web.
 */
export async function openNavigation(address: string): Promise<void> {
  if (!address) return;

  // Detect platform
  let platform = "web";
  try {
    const { Capacitor } = await import("@capacitor/core");
    platform = Capacitor.getPlatform();
  } catch {
    // not in Capacitor context
  }

  const provider = platform === "ios" ? "apple" : "google";
  await openInNativeMapApp(address, provider);
}

/**
 * Opens a specific map app (Apple Maps or Google Maps) for navigation.
 * On native platforms, uses URL schemes to launch the actual app
 * instead of opening a web browser.
 */
export async function openInNativeMapApp(
  address: string,
  provider: "apple" | "google"
): Promise<void> {
  if (!address) return;
  const encoded = encodeURIComponent(address);

  // Detect platform
  let platform = "web";
  try {
    const { Capacitor } = await import("@capacitor/core");
    platform = Capacitor.getPlatform();
  } catch {}

  const isNative = platform === "ios" || platform === "android";

  let url: string;

  if (provider === "apple") {
    // maps:// scheme opens Apple Maps app directly on iOS
    // On web/Android, fall back to HTTPS which still works
    url = isNative && platform === "ios"
      ? `maps://maps.apple.com/?daddr=${encoded}`
      : `https://maps.apple.com/?daddr=${encoded}`;
  } else {
    // comgooglemaps:// opens Google Maps app on iOS if installed
    // google.navigation: intent opens Google Maps on Android
    if (isNative && platform === "android") {
      url = `google.navigation:q=${encoded}`;
    } else if (isNative && platform === "ios") {
      url = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
    }
  }

  if (isNative) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url, windowName: "_system" });
    } catch {
      // Fallback to HTTPS if scheme fails (e.g. Google Maps not installed on iOS)
      const fallbackUrl = provider === "apple"
        ? `https://maps.apple.com/?daddr=${encoded}`
        : `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
    }
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Removes phone numbers and email addresses from a contact string,
 * leaving just the person/company name. Used on invoices so contact
 * details are never printed on customer-facing documents.
 */
export function stripContactDetails(raw?: string | null): string {
  if (!raw) return "";
  return raw
    .replace(/\S+@\S+\.\S+/g, "")
    .replace(/\+?\d[\d\s()-]{8,}/g, "")
    .replace(/\s*[-–—]\s*[-–—]\s*/g, " ")
    .replace(/\s*[-–—]\s*$/g, "")
    .replace(/^\s*[-–—]\s*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
